import { Divider, MenuItem, MenuList, Stack, Typography } from "@mui/material";
import { ReactNode, useState } from "react";
import { UIViewEnum } from '../../common/enums/UIViewEnum';
import { useUserContext } from '../../contexts/user-context/UserContext';
import { useRouteContext } from '../../hooks/use-route-context/UseRouteContext';
import { useAvailableObjects } from '../../queries/breadcrumb/UseAvailableObjects';
import { useQueryNotifications } from '../../queries/UseQueryNotifications';
import { IRoute } from '../../routes/routes';
import { INavigationItem } from "../../services/navigation-data-service/Models";
import NavigationFilterInput from "../navigation-filter/NavigationFilterInput";

export type NavigationObjectListProps = {
    item: INavigationItem;  // item for the current breadcrumb level, used for determining which objects to show in the list based on the view's settings
    route: IRoute;          // route object for the current view, used for determining which objects to show in the list based on the view's settings
    offline: boolean;
    enabled: boolean;
    actions?: ReactNode;
    onClick: ((item: INavigationItem, route: IRoute, shiftKey: boolean, ctrlKey: boolean) => void) | undefined;
};

export default function NavigationObjectList({
    item,
    route,
    offline,
    enabled,
    actions,
    onClick
}: NavigationObjectListProps) {
    const [filterText, setFilterText] = useState<string>("");
    const [userState] = useUserContext();
    const routeContext = useRouteContext();

    const {
        data
    } = useQueryNotifications(useAvailableObjects)({
        customerID: userState.customer?.id as number,
        contextGroupID: routeContext.groupID as number,
        contextObject: {
            id: item.objectID,
            objectType: item.objectTypeID
        },
        viewID: route.viewID as UIViewEnum,
        options: {
            wellID: routeContext.wellID,
            deviceID: routeContext.deviceID,
            tankBatteryID: routeContext.tankBatteryID,
            tankID: routeContext.tankID,
            tagID: routeContext.tagID,
        },
        filterText: filterText,
        offline: offline,
        enabled: enabled
    });

    return (
        <Stack
            direction="column"
            sx={{
                flex: "1 1 auto",
                height: 1,
                overflow: "hidden"
            }}
        >
            <Stack
                direction="row"
                sx={{
                    alignItems: "center"
                }}
            >
                <NavigationFilterInput
                    value={filterText}
                    onChange={setFilterText}
                    sx={{
                        marginLeft: 2,
                        marginRight: 2,
                        marginTop: 1,
                        marginBottom: 1,
                        flex: "1 1 auto"
                    }}
                    autoFocus={true}
                />
                {actions}
            </Stack>
            <Divider />
            <MenuList
                sx={{
                    overflowY: "auto",
                    flex: "1 1 auto"
                }}
            >
                {data?.filter(o => o.objectTypeID === item.objectTypeID)?.map((o, index) => (
                    <MenuItem
                        key={index}
                        sx={{
                            fontStyle: o.isObjectDisabled ? 'italic' : undefined,
                            textDecoration: o.isHidden ? 'line-through' : undefined,
                            "&:hover": {
                                textDecoration: o.isHidden ? 'line-through' : undefined,
                                cursor: "pointer"
                            }
                        }}
                        selected={o.objectID === item.objectID}
                        onClick={
                            event => onClick?.(o, route, event.shiftKey, event.ctrlKey)}
                    >
                        <Typography
                            variant="body1"
                            sx={{
                                textOverflow: "ellipsis",
                                whiteSpace: "nowrap",
                                overflow: "hidden"
                            }}
                        >
                            {o.title}
                        </Typography>
                    </MenuItem>
                ))}
            </MenuList>
        </Stack>
    );
}
